/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

// A form drafted by the AI from an existing one (AssistantFormDrafter.cs in the
// OCR service) turned into a builder form. The service has already cut the
// answer down to what a form may hold; this side checks again, since the
// builder is what saves it, and gives every field a key of its own.

import type { FormDraft, FormDraftField } from '../assistant/assistant.service';
import {
  FIELD_TYPES,
  FormColumn,
  FormColumnType,
  FormDefinition,
  FormField,
  FormFieldType,
  hasOptions,
  newField,
  newForm,
  newSection
} from './forms.model';

/** What the builder is told when it opens a draft: the draft, and where it came from. */
export interface FormDraftHandover {
  form: FormDefinition;
  source: string;
  model?: string;
}

/** The key in the router's navigation state that carries a draft to /forms/new. */
export const DRAFT_STATE = 'acaFormDraft';

const DRAFTABLE = new Set<string>(FIELD_TYPES.map((t) => t.type).filter((t) => t !== 'calc'));
const COLUMN_TYPES = new Set<string>(['text', 'number', 'money', 'date']);

export function draftToForm(draft: FormDraft): FormDefinition {
  const form = newForm();
  form.title = draft.title?.trim() ?? '';
  form.description = draft.description?.trim() ?? '';
  form.header = { organisation: draft.organisation?.trim() ?? '', subtitle: '' };
  form.footer = { signatures: (draft.signatures ?? []).map((s) => s.trim()).filter(Boolean), note: '' };
  form.sections = [];

  (draft.sections ?? []).forEach((given, i) => {
    const section = newSection(i + 1);
    section.title = given.title?.trim() ?? '';
    section.columns = given.columns === 1 ? 1 : 2;
    form.sections.push(section);
    for (const field of given.fields ?? []) {
      // each field is added before the next is made, so newField sees every key taken so far
      const made = fieldOf(field, form);
      if (made) {
        section.fields.push(made);
      }
    }
  });
  form.sections = form.sections.filter((s) => s.fields.length);
  if (!form.sections.length) {
    form.sections = [newSection(1)];
  }
  return form;
}

function fieldOf(given: FormDraftField, form: FormDefinition): FormField | null {
  const label = given.label?.trim() ?? '';
  if (!label) {
    return null;
  }
  let type: FormFieldType = DRAFTABLE.has(given.type) ? (given.type as FormFieldType) : 'text';
  const options = [...new Set((given.options ?? []).map((o) => o.trim()).filter(Boolean))];
  const columns = (given.columns ?? []).filter((c) => c.label?.trim());
  if (hasOptions(type) && options.length < 2) {
    type = 'text';
  }
  if (type === 'table' && !columns.length) {
    type = 'textarea';
  }

  const field = newField(type, form, label);
  field.required = type !== 'paragraph' && given.required === true;
  if (given.help?.trim()) {
    field.help = given.help.trim();
  }
  if (hasOptions(type)) {
    field.options = options;
  }
  if (type === 'table') {
    field.columns = columns.map(
      (c, i): FormColumn => ({ key: `col${i + 1}`, label: c.label.trim(), type: (COLUMN_TYPES.has(c.type) ? c.type : 'text') as FormColumnType })
    );
  }
  if (type === 'paragraph') {
    field.text = given.text?.trim() || label;
  }
  return field;
}

/** A draft handed over in navigation state, if it looks like one: history.state is whatever the last navigation left there. */
export function handedDraft(state: unknown): FormDraftHandover | null {
  const handover = (state as Record<string, unknown> | null)?.[DRAFT_STATE] as FormDraftHandover | undefined;
  const form = handover?.form;
  return form && typeof form.title === 'string' && Array.isArray(form.sections) ? handover : null;
}
